// src/components/ProductCard.jsx

import React, { useState, useEffect, useRef } from 'react';
import styles from './ProductCard.module.css';
import { motion } from 'framer-motion';
import ProductImage from '../components/ProductImage';

// 1) Translation strings for English and Arabic
const translations = {
  en: {
    noImage: 'No Image',
    loadingImage: 'Loading image...',
    imageUnavailable: 'Image unavailable',
    sizeLabel: 'Size:',
    skuLabel: 'SKU:',
    selectSort: 'Select a sort',
    selectColor: 'Select a color',
    quantityPlaceholder: 'Quantity',
  },
  ar: {
    noImage: 'لا توجد صورة',
    loadingImage: 'جارٍ تحميل الصورة...',
    imageUnavailable: 'الصورة غير متاحة',
    sizeLabel: 'الحجم:',
    skuLabel: 'رمز المنتج (SKU):',
    selectSort: 'اختر الصنف',
    selectColor: 'اختر اللون',
    quantityPlaceholder: 'الكمية',
  },
};

const ProductCard = ({
  productId,
  hasImage,
  name,
  size,
  sku,
  sorts = [],
  onSelectionChange,
  action,
  language = 'en',
}) => {
  const t = (key) => translations[language][key];

  const [selectedSortName, setSelectedSortName] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState('');
  const isFirstRender = useRef(true); // Track the first render

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (selectedSortName && selectedColor && selectedQuantity) {
      // Notify parent only when sortName, color, and quantity are selected
      onSelectionChange({
        productId, // Use productId instead of SKU
        name,
        sortName: selectedSortName, 
        color: selectedColor,
        quantity: parseInt(selectedQuantity, 10) || 0, // Ensure the quantity is a number
      });
    }
  }, [selectedSortName, selectedColor, selectedQuantity, productId, name, onSelectionChange]);

  // Find the selected sort object
  const selectedSortObj = sorts.find((sortObj) => sortObj.sortName === selectedSortName);

  return (
    <motion.article
      className={styles.productCard}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <div className={styles.productInfo}>
        <ProductImage
          productId={productId}
          hasImage={Boolean(hasImage)}
          alt={`${name}`}
          className={styles.productImage}
          placeholderClassName={styles.placeholderImage}
          loadingLabel={t('loadingImage')}
          noImageLabel={t('noImage')}
          errorLabel={t('imageUnavailable')}
        />

        <div className={styles.productDetails}>
          <h2 className={styles.productName}>{name}</h2>
          <div className={styles.productMeta}>
            <span className={styles.metaChip}>
              {t('sizeLabel')} {size}
            </span>
            <span className={styles.metaChip}>
              {t('skuLabel')} {sku}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.productOptions}>
        <label className={styles.fieldLabel} htmlFor={`sort-select-${productId}`}>
          {t('selectSort')}
        </label>
        <select
          id={`sort-select-${productId}`}
          className={styles.sortDropdown}
          value={selectedSortName}
          onChange={(e) => {
            setSelectedSortName(e.target.value);
            setSelectedColor(''); // Reset color when sort changes
            setSelectedQuantity('');
          }}
        >
          <option value="">{t('selectSort')}</option>
          {sorts.map((sortObj, index) => (
            <option key={sortObj.sortName || index} value={sortObj.sortName}>
              {sortObj.sortName}
            </option>
          ))}
        </select>

        <label className={styles.fieldLabel} htmlFor={`color-select-${productId}`}>
          {t('selectColor')}
        </label>
        <select
          id={`color-select-${productId}`}
          className={styles.colorDropdown}
          value={selectedColor}
          onChange={(e) => setSelectedColor(e.target.value)}
          disabled={!selectedSortName}
        >
          <option value="">{t('selectColor')}</option>
          {(selectedSortObj?.colors || []).map((colorObj, cIndex) => (
            <option key={colorObj.color || cIndex} value={colorObj.color}>
              {colorObj.color}
            </option>
          ))}
        </select>

        <label className={styles.fieldLabel} htmlFor={`quantity-input-${productId}`}>
          {t('quantityPlaceholder')}
        </label>
        <input
          id={`quantity-input-${productId}`}
          type="number"
          className={styles.countInput}
          placeholder={t('quantityPlaceholder')}
          min="1"
          value={selectedQuantity}
          onChange={(e) => setSelectedQuantity(e.target.value)}
          disabled={!selectedColor}
        />
      </div>
    </motion.article>
  );
};

export default ProductCard;
